import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactEcharts from "echarts-for-react";
import { getGeoJson } from "api";

import Citys from "assets/json/city.json";
import Adcodes from "assets/json/adcode.json";
import CityAdcodes from "assets/json/adcode-city.json";

import mapTexture from "assets/images/texture-map.jpg";
import tooltipBg from "assets/images/bg-tooltip.png";
import iconBack from "assets/images/icon-back.png";

const echarts = require("echarts");

export type DataType = {
  name: keyof typeof Citys;
  value: number;
};

type ProvinceType = keyof typeof Adcodes;
type CityType = keyof typeof CityAdcodes;

export interface MapProps {
  data: DataType[];
  mapName: ProvinceType;
  onMapChange: (name: ProvinceType) => void;
}

const initMap = (name: ProvinceType) =>
  new Promise((res, rej) => {
    if (echarts.getMap(name)) {
      res();
      return;
    }

    getGeoJson(`${Adcodes[name]}`)
      .then((geoJson) => {
        echarts.registerMap(name, geoJson, {});
        res();
      })
      .catch(() => rej());
  });

// 生成城市小点数据
const cityData = Object.entries(Citys)
  // .filter(() => Math.random() < 0.1) // 随机取一定比例
  .filter((e, i) => (i + 3) % 7 === 0) // 每7个城市取第三个 单纯是因为数量和位置比较OK
  .map(([name, value]) => ({
    name,
    value: [...value, 3 + Math.random() * 2], // 随机点大小
    itemStyle: {
      normal: {
        // color: Math.random() > 0.5 ? "#4cf2ff" : "#d167e6", // 随机颜色
        color: "#4cf2ff",
      },
    },
  }));

function getTexture(w: number, h: number) {
  const tmpImg = new Image();
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = w;
  canvas.height = h;
  tmpImg.onload = () => {
    ctx?.drawImage(tmpImg, 0, 0, canvas.width, canvas.height);
  };
  tmpImg.src = mapTexture;
  return canvas;
}

const texture = getTexture(window.innerWidth * 0.4, window.innerHeight * 0.65);

const getOption = (map: string, data: DataType[] = []) => {
  const isChina = map === "全国";
  // 附带坐标的城市数据
  const convertData = (data: DataType[]) =>
    data
      .filter((d) => Citys.hasOwnProperty(d.name))
      .map((d) => ({
        name: d.name,
        value: Citys[d.name].concat(d.value),
      }));

  // 数据最大值
  const maxValue = Math.max(...data.map((d) => d.value), 0);
  // 数据最大值的位数
  const digitsOfMaxValue = String(maxValue).length;

  // 计算TOP20点的大小
  const valueToSize = (value = 0) =>
    Math.pow(value / Math.pow(10, digitsOfMaxValue - 2), 0.5) + 5;

  const option = {
    tooltip: {
      trigger: "item",
      alwaysShowContent: true,
    },
    geo: [
      // 边框
      {
        map,
        roam: false,
        zoom: 1.05,
        ...(isChina
          ? {
              top: "24%",
              right: "24%",
              left: "12%",
            }
          : {
              top: "10%",
            }),
        itemStyle: {
          normal: {
            shadowColor: "rgba(191, 169, 255, 0.12)",
            shadowBlur: 8,
            areaColor: "transparent",
            borderColor: "#8373c5",
            borderWidth: 1,
          },
        },
        animation: false,
        silent: true,
        zlevel: 1,
        z: 2,
      },
      // 底色
      {
        map,
        roam: false,
        zoom: 1.05,
        ...(isChina
          ? {
              top: "25%",
              right: "24%",
              left: "12%",
            }
          : {
              top: "11%",
            }),
        itemStyle: {
          normal: {
            areaColor: {
              type: "linear",
              x: 0,
              y: 500,
              x2: 0,
              y2: 0,
              colorStops: [
                { offset: 0, color: "#8783d1" },
                { offset: 1, color: "#cec1ff" },
              ],
              global: true,
            },
            borderColor: "transparent",
          },
        },
        animation: false,
        silent: true,
        zlevel: 1,
        z: 1,
      },
    ],
    series: [
      // 城市 TOP20
      {
        name: "Top",
        type: "effectScatter",
        coordinateSystem: "geo",
        showEffectOn: "emphasis",
        rippleEffect: {
          color: "#4cf2ff",
          scale: 3,
        },
        symbolSize: (v: number[]) => valueToSize(v[2]),
        itemStyle: {
          normal: {
            color: "rgba(127, 244, 246, 1)",
            borderColor: "rgba(127, 244, 246, 0.4)",
            borderWidth: 8,
          },
          emphasis: {
            color: "#fff",
            borderColor: "transparent",
          },
        },
        animation: false,
        zlevel: 10,
        tooltip: {
          padding: 0,
          backgroundColor: "tranparent",
          formatter: ({ name, value }: { name: string; value: number[] }) => `
            <div class="relative">
              <img src=${tooltipBg} style="width: 10.3vw; height: 3.125vw" />
              <div class="absolute text-2xl font-s font-bold" style="top: 0; left: 75%; transform: translate(-50%, -75%)">${name}</div>
              <div class="absolute text-3xl font-n" style="top: 58%; left: 50%; transform: translate(-50%, -50%)">${value[2].toLocaleString()}</div>
            </div>`,
        },
        data: convertData(data.sort((a, b) => b.value - a.value)),
      },
      // 城市小点
      ...(isChina
        ? [
            {
              name: "City",
              type: "scatter",
              coordinateSystem: "geo",
              animation: false,
              symbolSize: (v: number[]) => v[2],
              tooltip: {
                show: false,
              },
              zlevel: 5,
              silent: true,
              data: cityData,
            },
          ]
        : []),
      {
        name: "Map",
        type: "map",
        map,
        tooltip: {
          show: false,
        },
        label: {
          emphasis: {
            show: false,
          },
        },
        roam: false,
        zoom: 1.05,
        ...(isChina
          ? {
              top: "24%",
              right: "24%",
              left: "12%",
            }
          : {
              top: "10%",
            }),
        itemStyle: {
          normal: {
            areaColor: {
              image: texture,
            },
            borderColor: "#8373c5",
            borderWidth: 0.5,
          },
          emphasis: {
            areaColor: {
              image: texture,
            },
            borderColor: "#d4cbff",
            borderWidth: 1,
          },
        },
        animation: false,
        zlevel: 2,
      },
    ],
  };

  return option;
};

const Map: React.FC<MapProps> = ({ data, mapName, onMapChange }) => {
  const [option, setOption] = useState<any>({});
  const [current, setCurrent] = useState(0);
  const ref = useRef(null);

  const onEvents = useMemo(
    () => ({
      click: ({ name }: { name: CityType }) => {
        // 暂无台湾地图
        if (name === "台湾省") {
          return;
        }
        // 通过市级Adcode找到省级Adcode
        const code = CityAdcodes[name];
        const [newName] =
          Object.entries(Adcodes).find(
            ([k, v]) => String(v / 10000) === String(code).slice(0, 2)
          ) || [];
        if (newName) {
          onMapChange(newName as ProvinceType);
        }
      },
    }),
    [onMapChange]
  );

  useEffect(() => {
    initMap(mapName)
      .then(() => {
        setOption(getOption(mapName, data));
      })
      .catch(() => {
        alert("地图获取失败");
      });
  }, [mapName, data]);

  useEffect(() => {
    const myChart = (ref.current as any).getEchartsInstance();
    const d = data?.sort((a, b) => b.value - a.value) ?? [];
    const length = d.length;

    const timer = setTimeout(() => {
      // 取消之前高亮的图形
      myChart.dispatchAction({
        type: "downplay",
      });
      myChart.dispatchAction({
        type: "hideTip",
      });
      if (length <= 0) {
        return;
      }
      // 高亮地图
      if (mapName !== "全国") {
        myChart.dispatchAction({
          type: "highlight",
          seriesName: "Map",
          name: d[current % length].name,
        });
      }
      // 高亮当前图形
      myChart.dispatchAction({
        type: "highlight",
        seriesName: "Top",
        name: d[current % length].name,
      });
      // 显示 tooltip
      myChart.dispatchAction({
        type: "showTip",
        seriesIndex: 0,
        name: d[current % length].name,
      });
      setCurrent((pre) => pre + 1);
    }, 3200);

    return () => {
      clearTimeout(timer);
    };
  }, [current, data, mapName]);

  const handleBack = () => onMapChange("全国");

  return (
    <>
      <ReactEcharts
        option={option}
        notMerge={true}
        className="absolute top-0 left-0 w-full"
        style={{ height: "100%" }}
        ref={ref}
        onEvents={onEvents}
      />
      {mapName !== "全国" && (
        <button
          className="absolute top-0 right-0 w-12 h-12 focus:outline-none"
          onClick={handleBack}
        >
          <img src={iconBack} alt="" />
        </button>
      )}
    </>
  );
};

export default Map;
